import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useDocumentHead } from '@/hooks/use-document-head';
import { FormContact } from '@/components/forms/FormContact';
import { ScheduleForm } from './components/schedule-form';

const SERVICE_AREAS = [
  'Orlando', 'Winter Park', 'Altamonte Springs', 'Casselberry', 'Lake Mary',
  'Sanford', 'Oviedo', 'Apopka', 'Kissimmee', 'Ocoee', 'Winter Garden', 'Longwood',
];

export default function ContactPage() {
  useDocumentHead(
    'Contact SOTSVC | Sonz of Thunder Services Orlando',
    'Contact Sonz of Thunder Services (SOTSVC) for cleaning in Orlando & Central Florida. Call (407) 461-6039 for a free quote.',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] clip-diag-bottom overflow-hidden">
        <img src="/images/heroes/hero-7-twilight-lobby.webp" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.9) 0%, rgba(15,15,30,.55) 60%, rgba(0,180,216,.25) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// GET IN TOUCH</span>
            </div>
            <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88]">
              CONTACT <span className="text-thunder">US</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at right, rgba(0,180,216,.1), transparent 70%)' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <ScrollReveal direction="left">
            <div className="text-electric font-display tracking-[0.4em] text-xs mb-4">// DROP THE BOOM</div>
            <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
              TALK TO <span className="text-thunder">THUNDER.</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              Fill out the form. A team member responds within one business hour. Or skip the form and just call — a human picks up.
            </p>

            <div className="space-y-6 mb-12">
              <a href="tel:4074616039" className="flex items-center gap-5 group">
                <div className="w-14 h-14 border-2 border-electric flex items-center justify-center text-electric text-xl group-hover:bg-electric group-hover:text-charcoal-deep transition skew-card">
                  <span className="skew-inner">&#x2706;</span>
                </div>
                <div>
                  <div className="font-display text-electric tracking-[0.3em] text-xs">PHONE</div>
                  <div className="font-display text-white text-2xl group-hover:text-thunder transition">(407) 461-6039</div>
                </div>
              </a>

              <a href="mailto:dalvarez@sotsvc.com" className="flex items-center gap-5 group">
                <div className="w-14 h-14 border-2 border-electric flex items-center justify-center text-electric text-xl group-hover:bg-electric group-hover:text-charcoal-deep transition skew-card">
                  <span className="skew-inner">&#x2709;</span>
                </div>
                <div>
                  <div className="font-display text-electric tracking-[0.3em] text-xs">EMAIL</div>
                  <div className="font-display text-white text-xl sm:text-2xl group-hover:text-thunder transition break-all">dalvarez@sotsvc.com</div>
                </div>
              </a>

              <div className="flex items-center gap-5">
                <div className="w-14 h-14 border-2 border-electric flex items-center justify-center text-electric text-xl skew-card">
                  <span className="skew-inner">&#x26B2;</span>
                </div>
                <div>
                  <div className="font-display text-electric tracking-[0.3em] text-xs">HOURS</div>
                  <div className="font-display text-white text-2xl">Sun–Fri · 7am–10pm</div>
                </div>
              </div>
            </div>

            {/* Service Areas */}
            <div className="border-t border-white/10 pt-8">
              <div className="text-thunder font-display tracking-[0.3em] text-xs mb-4">// SERVICE AREAS</div>
              <div className="flex flex-wrap gap-2">
                {SERVICE_AREAS.map((area) => (
                  <span key={area} className="border border-electric/30 text-white/70 font-display tracking-wider text-xs px-3 py-1">
                    {area.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right">
            <FormContact />
          </ScrollReveal>
        </div>
      </section>

      {/* Schedule a Cleaning */}
      <ScheduleForm />

      {/* Find Us */}
      <section className="relative py-20 bg-charcoal overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-electric font-display tracking-[0.4em] text-xs mb-4 text-center">// OUR LOCATION</div>
          <h2 className="font-display font-bold text-white text-4xl sm:text-5xl leading-[0.9] mb-10 text-center">
            FIND <span className="text-thunder">US</span>
          </h2>
          <div className="w-full max-w-[800px] mx-auto aspect-[4/3] [&>iframe]:w-full [&>iframe]:h-full">
            <iframe
              title="Sonz of Thunder Services location map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1795352.9134898128!2d-81.646989!3d28.493136099999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x20be2997b33eea83%3A0x8ae3fcd520d9a73f!2sSonz%20of%20Thunder%20SVC%20LLC!5e0!3m2!1sen!2sus!4v1789264921925!5m2!1sen!2sus"
              width="600"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
